import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { X, Copy, Check, Share2, QrCode, Image, Layers, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { NotificationType } from '../../../constants/Enums';
import useScrollLock from '../../../hooks/useScrollLock';
import { buildWhatsAppOrderUrl, buildGoogleMapsLink, DEFAULT_ORDER_MESSAGE } from '../../../services/helpers/ContactHelper';
import BusinessApiService from '../../../services/apiServices/BusinessApiService';
import { saveAddressOnly } from '../../../services/helpers/ProfileHelper';
import { AppDispatch } from '../../../dataManagement/uiDb/UiDb';
import { openAuth } from '../../../dataManagement/uiDb/GeneralDb';
import { loadGoogleMapsAPI } from '../PlaceSearch/helpers/PlacesSearchHelper';
import { REACT_APP_GOOGLE_MAPS_API_KEY } from '../../../constants/EnvVariables';
import LocationPinPicker from './childComponents/LocationPinPicker';
import { SESSION_PENDING_WA_INTENT, SESSION_PENDING_WA_ADDRESS } from '../../../constants/SessionKeys';
import NineFindsOrderButton from './childComponents/NineFindsOrderButton';
import WhatsAppIcon from '../icons/WhatsAppIcon';
import './styles/ShareProfileModal.css';

interface ShareProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileUrl: string;
  userName?: string;
  title?: string;
  subtitle?: string;
  productImages?: string[];
  shareMode?: 'qr' | 'images' | 'both';
  businessPhone?: string;
  /** The businessId to register this customer against via add-customer-to-business. */
  businessId?: string;
  initialStep?: ShareStep;
  /** Pre-fill the guest address field — used after post-login intent restoration. */
  initialGuestAddress?: string;
  /** Collection total — passed to a NineFinds order when ordering is enabled. */
  totalPrice?: number;
  /** Business owner enabled in-app DELIVERY orders (NineFinds). */
  enableOnlineOrdering?: boolean;
  /** Business owner enabled in-app ON_PREMISE orders (NineFinds). */
  enableOfflineOrdering?: boolean;
  /** Which order flow this modal drives when opened at the whatsapp step. */
  orderMode?: 'whatsapp' | 'nine-delivery' | 'nine-onpremise';
}

type ShareStep = 'options' | 'qr' | 'whatsapp' | 'location-pin';

const ShareProfileModal: React.FC<ShareProfileModalProps> = ({
  isOpen,
  onClose,
  profileUrl,
  userName,
  title,
  subtitle,
  productImages = [],
  shareMode,
  businessPhone,
  businessId,
  initialStep,
  initialGuestAddress,
  totalPrice,
  enableOnlineOrdering,
  enableOfflineOrdering,
  orderMode = 'whatsapp',
}) => {
  const { triggerNotification } = useNotification();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userAddress = useSelector((s: any) => s.profile.data?.address as string | undefined);
  const profileData = useSelector((s: any) => s.profile.data);
  const isSavingAddress = useSelector((s: any) => !!s.profile.loadingMap?.profileUpdate);
  const [addressSaved, setAddressSaved] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [editedAddress, setEditedAddress] = useState('');
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState<ShareStep>('options');
  const [enteredAtWhatsapp, setEnteredAtWhatsapp] = useState(false);
  const [loadingType, setLoadingType] = useState<'qr' | 'images' | 'both' | null>(null);
  const [customMessage, setCustomMessage] = useState('');
  const [messageMode, setMessageMode] = useState<'predefined' | 'custom'>('predefined');
  const [guestAddress, setGuestAddress] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [pinCoords, setPinCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [pinAddress, setPinAddress] = useState<string | null>(null);
  const [mapsApiState, setMapsApiState] = useState<'loading' | 'ready' | 'failed'>('loading');

  /** Phone from profile (logged-in) or guest input (stripped of non-digits, without country code) */
  const userPhone: string | null = profileData?.phone ?? null;
  const effectivePhone: string | null = userPhone
    ? userPhone.replace(/\D/g, '')
    : guestPhone.trim()
    ? guestPhone.replace(/\D/g, '')
    : null;

  // The address that will be used in the order message right now.
  const currentEffectiveAddress = editingAddress ? editedAddress.trim() : (userAddress || guestAddress.trim());
  // Pin is only "valid" (used in order, shown in chip) if it was set for the current address.
  const isPinSet = pinCoords !== null && pinAddress === currentEffectiveAddress;

  const hiddenQrRef = React.useRef<HTMLDivElement>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);

  useScrollLock(isOpen, overlayRef);

  React.useEffect(() => {
    if (isOpen) {
      setCopied(false);
      setLoadingType(null);
      setCustomMessage('');
      setMessageMode('predefined');
      setGuestAddress(initialGuestAddress ?? '');
      setGuestPhone('');
      setAddressSaved(false);
      setEditingAddress(false);
      setEditedAddress('');
      setPinCoords(null);
      setPinAddress(null);

      if (initialStep) {
        setStep(initialStep);
        setEnteredAtWhatsapp(initialStep === 'whatsapp');
      } else if (productImages.length > 0) {
        setStep('options');
        setEnteredAtWhatsapp(false);
      } else {
        setStep('qr');
        setEnteredAtWhatsapp(false);
      }
    }
  }, [isOpen, productImages.length, initialStep]);

  // Preload the Maps API so the delivery-pin picker is ready by the time the
  // user reaches the WhatsApp step. Only relevant when the WhatsApp step is
  // reachable at all. If it can't load within a few seconds, fall back to
  // allowing orders without a precise pin.
  useEffect(() => {
    // Preload for the WhatsApp order pin, and also for NineFinds delivery orders
    // (which need a pin even when the business has no WhatsApp phone).
    if (!isOpen || (!businessPhone && !enableOnlineOrdering)) return;
    if (window.google?.maps) {
      setMapsApiState('ready');
      return;
    }
    setMapsApiState('loading');
    const timer = setTimeout(() => {
      setMapsApiState((prev) => (prev === 'loading' ? 'failed' : prev));
    }, 4000);
    loadGoogleMapsAPI(REACT_APP_GOOGLE_MAPS_API_KEY, () => {
      clearTimeout(timer);
      // Don't flip back to 'ready' if we already told the user the map is
      // unavailable and relaxed the pin requirement — re-imposing it now
      // would disable the order button out from under them.
      setMapsApiState((prev) => (prev === 'failed' ? prev : 'ready'));
    });
    return () => clearTimeout(timer);
  }, [isOpen, businessPhone, enableOnlineOrdering]);

  if (!isOpen) return null;

  const buildShareText = (): string => {
    if (subtitle) return subtitle;
    if (title) return 'Check out ' + title + ' on NineFinds';
    return '';
  };

  const copyToClipboard = async (text: string): Promise<boolean> => {
    // Try modern API first
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn('Clipboard API failed:', err);
      }
    }

    // Fallback for insecure contexts (HTTP) or older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      console.error('Fallback copy failed:', err);
      return false;
    }
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(profileUrl);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      triggerNotification('Failed to copy link', NotificationType.ERROR);
    }
  };

  const getQrFile = async (): Promise<File | null> => {
    const container = hiddenQrRef.current;
    if (!container) return null;
    const qrCanvas = container.querySelector('canvas');
    if (!qrCanvas) return null;

    const padding = 40;
    const textHeight = 60;
    const composite = document.createElement('canvas');
    const ctx = composite.getContext('2d');
    if (!ctx) return null;

    composite.width = qrCanvas.width + padding * 2;
    composite.height = qrCanvas.height + padding * 2 + textHeight;
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, composite.width, composite.height);
    ctx.drawImage(qrCanvas, padding, padding);
    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    const label = (userName && userName !== 'Me')
      ? '@' + userName.replace(/\s+/g, '').toLowerCase()
      : (title || 'NineFinds');

    // Truncate label if it's too long for the canvas width
    const maxWidth = composite.width - 40;
    let displayLabel = label;
    if (ctx.measureText(label).width > maxWidth) {
      let len = label.length;
      while (ctx.measureText(displayLabel + '...').width > maxWidth && len > 0) {
        len--;
        displayLabel = label.substring(0, len);
      }
      displayLabel += '...';
    }

    ctx.fillText(displayLabel, composite.width / 2, qrCanvas.height + padding + textHeight / 1.5);

    const blob = await new Promise<Blob | null>(resolve => composite.toBlob(resolve, 'image/png', 1.0));
    if (!blob) return null;
    return new File([blob], 'nine-finds-qr-code.png', { type: 'image/png' });
  };

  const getImageFiles = async (): Promise<File[]> => {
    const files: File[] = [];
    if (productImages.length > 0) {
      try {
        const res = await fetch(productImages[0]);
        const blob = await res.blob();
        const ext = blob.type.includes('png') ? 'png' : 'jpg';
        files.push(new File([blob], 'product-image.' + ext, { type: blob.type }));
      } catch (e) {
        console.warn('Could not fetch product image:', productImages[0], e);
      }
    }
    return files;
  };

  const canFileShare = (files: File[]): boolean => {
    if (typeof navigator.share !== 'function') return false;
    if (typeof (navigator as any).canShare !== 'function') return false;
    return Boolean((navigator as any).canShare({ files }));
  };

  const doShare = async (files: File[], shareText: string, isImageShare: boolean = false, nativeImageUrls?: string[]) => {
    const isNativeApp = navigator.userAgent.includes('NineFindsApp/Android');
    const nativeShare = isNativeApp
      && (window as any).AndroidBridge
      && typeof (window as any).AndroidBridge.canShareImages === 'function'
      && (window as any).AndroidBridge.canShareImages();

    // 1) Native app: hand images to the system share sheet
    if (nativeShare && isImageShare) {
      try {
        let urlsToShare = nativeImageUrls || [];

        // Convert any fallback files (like QR code) to data URLs if we don't have explicit urls
        if (urlsToShare.length === 0 && files.length > 0) {
           const dataUrls = await Promise.all(files.map(file => {
             return new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
             });
           }));
           urlsToShare = dataUrls;
        }

        if (urlsToShare.length > 0) {
          const textParam = [shareText, profileUrl].filter(Boolean).join('\n');
          
          if (urlsToShare.length === 1) {
            (window as any).AndroidBridge.shareImage(urlsToShare[0], textParam);
          } else {
            (window as any).AndroidBridge.shareImages(JSON.stringify(urlsToShare), textParam);
          }
          return;
        }
      } catch (err) {
         console.error('Native share failed', err);
      }
    }

    // 2) Real browser with Web Share Level 2
    const supportsShare = typeof navigator.share === 'function';
    const supportsFileShare = supportsShare && files.length > 0 && canFileShare(files);

    if (isImageShare && !supportsFileShare) {
      triggerNotification("Your browser doesn't support image sharing, so we've copied the link for you instead.", NotificationType.WARNING);
      // Fallback: copy URL as well
      const success = await copyToClipboard(profileUrl);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
      return;
    }

    if (supportsShare) {
      const shareData: ShareData = supportsFileShare
        ? { title: title || 'NineFinds', text: shareText, url: profileUrl, files }
        : { title: title || 'NineFinds', text: shareText, url: profileUrl };
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error('Share failed', err);
      }
    } else {
      const success = await copyToClipboard(profileUrl);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        triggerNotification('Link copied to clipboard', NotificationType.SUCCESS);
      } else {
        triggerNotification('Failed to share or copy link', NotificationType.ERROR);
      }
    }
  };

  const handleShareQr = async () => {
    setLoadingType('qr');
    try {
      const qrFile = await getQrFile();
      await doShare(qrFile ? [qrFile] : [], buildShareText(), true);
    } catch (err) {
      console.error('QR share failed:', err);
    } finally {
      setLoadingType(null);
    }
  };

  const handleShareImages = async () => {
    setLoadingType('images');
    try {
      const isNativeApp = navigator.userAgent.includes('NineFindsApp/Android');
      const nativeShare = isNativeApp
        && (window as any).AndroidBridge
        && typeof (window as any).AndroidBridge.canShareImages === 'function'
        && (window as any).AndroidBridge.canShareImages();

      if (nativeShare && productImages.length > 0) {
        await doShare([], buildShareText(), true, productImages);
      } else {
        const files = await getImageFiles();
        await doShare(files, buildShareText(), true);
      }
    } catch (err) {
      console.error('Image share failed:', err);
    } finally {
      setLoadingType(null);
    }
  };

  const handleShareBoth = async () => {
    setLoadingType('both');
    try {
      const isNativeApp = navigator.userAgent.includes('NineFindsApp/Android');
      const nativeShare = isNativeApp
        && (window as any).AndroidBridge
        && typeof (window as any).AndroidBridge.canShareImages === 'function'
        && (window as any).AndroidBridge.canShareImages();

      if (nativeShare) {
        const qrFile = await getQrFile();
        const urlsToShare = [...productImages];
        if (qrFile) {
          const qrDataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(qrFile);
          });
          urlsToShare.unshift(qrDataUrl);
        }
        await doShare([], buildShareText(), true, urlsToShare);
      } else {
        const [qrFile, imageFiles] = await Promise.all([getQrFile(), getImageFiles()]);
        const allFiles = qrFile ? [qrFile, ...imageFiles] : imageFiles;
        await doShare(allFiles, buildShareText(), true);
      }
    } catch (err) {
      console.error('Share both failed:', err);
    } finally {
      setLoadingType(null);
    }
  };

  const hasImages = productImages.length > 0;

  const qrSettings = {
    src: '/nine_logo_vector_4.svg',
    x: undefined,
    y: undefined,
    height: 48,
    width: 48,
    excavate: true,
  };

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  return createPortal(
    <div ref={overlayRef} className="share-profile-modal-overlay" onClick={onClose}>
      <div className="share-profile-modal-content" onClick={stopPropagation}>

        <button className="share-modal-close-btn" onClick={onClose}>
          <X size={24} />
        </button>

        <div ref={hiddenQrRef} className="share-modal-qr-hidden" aria-hidden="true">
          <QRCodeCanvas
            value={profileUrl}
            size={200}
            level="H"
            includeMargin={true}
            imageSettings={qrSettings}
          />
        </div>

        {step === 'options' && hasImages && (
          <>
            <h2 className="share-modal-title">{title || 'Share'}</h2>
            <p className="share-modal-subtitle">How would you like to share?</p>

            <div className="share-options-list">
              <button
                className="share-option-card"
                onClick={() => setStep('qr')}
              >
                <div className="share-option-icon share-option-icon--qr">
                  <QrCode size={22} />
                </div>
                <div className="share-option-text">
                  <span className="share-option-label">Share QR Code</span>
                  <span className="share-option-desc">Scannable code linking to this product</span>
                </div>
                <ChevronRight size={16} className="share-option-arrow" />
              </button>

              <button
                className={'share-option-card' + (!hasImages ? ' share-option-card--disabled' : '')}
                onClick={hasImages && !loadingType ? handleShareImages : undefined}
                disabled={!hasImages || !!loadingType}
              >
                <div className="share-option-icon share-option-icon--images">
                  <Image size={22} />
                </div>
                <div className="share-option-text">
                  <span className="share-option-label">Share Image</span>
                  <span className="share-option-desc">{hasImages ? 'Cover photo of this listing' : 'No image available'}</span>
                </div>
                {loadingType === 'images'
                  ? <div className="share-option-spinner" />
                  : <ChevronRight size={16} className="share-option-arrow" />}
              </button>

              <button
                className={'share-option-card' + (!hasImages ? ' share-option-card--disabled' : '')}
                onClick={hasImages && !loadingType ? handleShareBoth : undefined}
                disabled={!hasImages || !!loadingType}
              >
                <div className="share-option-icon share-option-icon--both">
                  <Layers size={22} />
                </div>
                <div className="share-option-text">
                  <span className="share-option-label">Share Both</span>
                  <span className="share-option-desc">{hasImages ? 'QR code + cover photo' : 'No image available'}</span>
                </div>
                {loadingType === 'both'
                  ? <div className="share-option-spinner" />
                  : <ChevronRight size={16} className="share-option-arrow" />}
              </button>

            </div>
          </>
        )}

        {step === 'qr' && (
          <>
            {hasImages && (
              <button className="share-modal-back-btn" onClick={() => setStep('options')}>
                <ChevronLeft size={18} />
                <span>Back</span>
              </button>
            )}

            <h2 className="share-modal-title">{title || (hasImages ? 'Share Listing' : 'Share Profile')}</h2>
            <p className="share-modal-subtitle">
              {subtitle || (userName === 'Me' ? 'Share your profile' : 'Share ' + (userName || '') + "'s profile")}
            </p>

            <div className="share-modal-qr-container">
              <div className="qr-wrapper">
                <QRCodeCanvas
                  value={profileUrl}
                  size={200}
                  level="H"
                  includeMargin={true}
                  imageSettings={qrSettings}
                />
              </div>
            </div>

            <div className="share-modal-link-box" onClick={handleCopy}>
              <span className="share-modal-link-text">{profileUrl}</span>
            </div>

            <div className="share-modal-actions">
              <button className="share-action-btn copy-btn" onClick={handleCopy} disabled={!!loadingType}>
                {copied ? <Check size={20} /> : <Copy size={20} />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
              <button className="share-action-btn share-native-btn" onClick={handleShareQr} disabled={!!loadingType}>
                {loadingType === 'qr' ? (
                  <div className="share-option-spinner" style={{ borderColor: '#cbd5e1', borderTopColor: '#0f172a' }} />
                ) : (
                  <Share2 size={20} />
                )}
                <span>{loadingType === 'qr' ? 'Preparing...' : 'Share'}</span>
              </button>
            </div>

            {businessPhone && (
              <button
                className="share-action-btn share-whatsapp-btn"
                onClick={() => setStep('whatsapp')}
                disabled={!!loadingType}
              >
                <WhatsAppIcon size={20} />
                <span>Order on WhatsApp</span>
              </button>
            )}
          </>
        )}

        {step === 'whatsapp' && (
          <>
            <button className="share-modal-back-btn" onClick={() => enteredAtWhatsapp ? onClose() : setStep('qr')}>
              <ChevronLeft size={18} />
              <span>Back</span>
            </button>

            <h2 className="share-modal-title">
              {orderMode === 'whatsapp'
                ? 'Order via WhatsApp'
                : orderMode === 'nine-delivery'
                ? 'Delivery order'
                : 'On-premise order'}
            </h2>
            <p className="share-modal-subtitle">
              {orderMode === 'whatsapp'
                ? 'Confirm your delivery address and message before sending'
                : orderMode === 'nine-delivery'
                ? 'Confirm your delivery address to place the order'
                : 'Review and place your order'}
            </p>

            {(orderMode === 'whatsapp' || orderMode === 'nine-delivery') && (
            <div className="share-wa-section">
              <p className="share-wa-label">Delivery Address</p>
              {userAddress && !editingAddress ? (
                <div className="share-wa-address-card">
                  <MapPin size={16} className="share-wa-address-icon" />
                  <span className="share-wa-address-text">{userAddress}</span>
                  <button
                    className="share-wa-edit-address-btn"
                    onClick={() => { setEditingAddress(true); setEditedAddress(userAddress); setAddressSaved(false); }}
                    aria-label="Edit address"
                  >
                    Edit
                  </button>
                </div>
              ) : userAddress && editingAddress ? (
                <>
                  <textarea
                    className="share-wa-textarea share-wa-guest-address"
                    value={editedAddress}
                    onChange={(e) => { setEditedAddress(e.target.value); setAddressSaved(false); }}
                    rows={2}
                    autoFocus
                  />
                  <div className="share-wa-address-actions">
                    <button
                      className="share-wa-cancel-edit-btn"
                      onClick={() => { setEditingAddress(false); setEditedAddress(''); }}
                    >
                      Cancel
                    </button>
                    {editedAddress.trim() && editedAddress.trim() !== userAddress && (
                      <button
                        className={`share-wa-save-address-btn ${addressSaved ? 'share-wa-save-address-btn--saved' : ''}`}
                        onClick={async () => {
                          try {
                            await saveAddressOnly(dispatch, profileData, editedAddress.trim());
                            setAddressSaved(true);
                            setEditingAddress(false);
                            setPinCoords(null);
                            setPinAddress(null);
                            triggerNotification('Address saved to your profile!', NotificationType.SUCCESS);
                          } catch {
                            triggerNotification('Could not save address. Please try again.', NotificationType.ERROR);
                          }
                        }}
                        disabled={isSavingAddress || addressSaved}
                      >
                        {isSavingAddress ? 'Saving...' : addressSaved ? '✓ Saved' : 'Save to profile'}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <textarea
                    className="share-wa-textarea share-wa-guest-address"
                    placeholder="Enter your delivery address..."
                    value={guestAddress}
                    onChange={(e) => { setGuestAddress(e.target.value); setAddressSaved(false); }}
                    rows={2}
                    autoFocus
                  />
                  {guestAddress.trim() && (
                    <button
                      className={`share-wa-save-address-btn ${addressSaved ? 'share-wa-save-address-btn--saved' : ''}`}
                      onClick={async () => {
                        if (!profileData) {
                          sessionStorage.setItem(SESSION_PENDING_WA_ADDRESS, guestAddress.trim());
                          sessionStorage.setItem(SESSION_PENDING_WA_INTENT, 'true');
                          onClose();
                          dispatch(openAuth(window.location.pathname + window.location.search));
                          return;
                        }
                        try {
                          await saveAddressOnly(dispatch, profileData, guestAddress.trim());
                          setAddressSaved(true);
                          triggerNotification('Address saved to your profile!', NotificationType.SUCCESS);
                        } catch {
                          triggerNotification('Could not save address. Please try again.', NotificationType.ERROR);
                        }
                      }}
                      disabled={isSavingAddress || addressSaved}
                    >
                      {isSavingAddress ? 'Saving...' : addressSaved ? '✓ Saved' : 'Save to profile'}
                    </button>
                  )}
                </>
              )}
            </div>
            )}

            {(orderMode === 'whatsapp' || orderMode === 'nine-delivery') && (
            <div className="share-wa-section">
              <p className="share-wa-label">
                Delivery Pin{mapsApiState !== 'failed' ? ' (required)' : ''}
              </p>
              {mapsApiState === 'loading' && (
                <div className="share-wa-pin-loading">
                  <div className="share-option-spinner" />
                  <span>Preparing map…</span>
                </div>
              )}
              {mapsApiState === 'failed' && (
                <p className="share-wa-pin-unavailable">
                  Map unavailable — continuing without a precise pin.
                </p>
              )}
              {mapsApiState === 'ready' && (
                isPinSet ? (
                  <div className="share-wa-pin-chip">
                    <MapPin size={16} className="share-wa-address-icon" />
                    <span className="share-wa-pin-text">Pin set</span>
                    <button className="share-wa-edit-address-btn" onClick={() => setStep('location-pin')}>
                      Edit
                    </button>
                    <button className="share-wa-remove-pin-btn" onClick={() => { setPinCoords(null); setPinAddress(null); }}>
                      Remove
                    </button>
                  </div>
                ) : (
                  <button className="share-wa-pin-btn" onClick={() => setStep('location-pin')}>
                    <MapPin size={16} />
                    Set exact delivery pin on map
                  </button>
                )
              )}
            </div>
            )}

            {orderMode === 'whatsapp' && !userPhone && (
              <div className="share-wa-section">
                <p className="share-wa-label">Your Mobile Number</p>
                <div className="share-wa-phone-row">
                  <span className="share-wa-phone-prefix">+91</span>
                  <input
                    className="share-wa-phone-input"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="Enter 10-digit number"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  />
                </div>
              </div>
            )}

            {orderMode === 'whatsapp' && (
            <div className="share-wa-section">
              <p className="share-wa-label">Message</p>
              <div className="share-wa-msg-toggle">
                <button
                  className={'share-wa-msg-tab' + (messageMode === 'predefined' ? ' share-wa-msg-tab--active' : '')}
                  onClick={() => setMessageMode('predefined')}
                >
                  Default
                </button>
                <button
                  className={'share-wa-msg-tab' + (messageMode === 'custom' ? ' share-wa-msg-tab--active' : '')}
                  onClick={() => setMessageMode('custom')}
                >
                  Custom
                </button>
              </div>
              {messageMode === 'predefined' ? (
                <p className="share-wa-predefined">{DEFAULT_ORDER_MESSAGE}</p>
              ) : (
                <textarea
                  className="share-wa-textarea"
                  placeholder="Write your message..."
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={3}
                  autoFocus
                />
              )}
            </div>
            )}

            {orderMode === 'nine-onpremise' && (
              <div className="share-wa-section share-wa-summary-row">
                <span className="share-wa-label">Order total</span>
                <span className="share-wa-summary-total">
                  {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(totalPrice ?? 0)}
                </span>
              </div>
            )}

            {orderMode === 'whatsapp' && (
            <button
              className="share-action-btn share-whatsapp-btn share-whatsapp-btn--full"
              disabled={
                !(editingAddress ? editedAddress.trim() : (userAddress || guestAddress.trim())) ||
                !businessPhone ||
                (!userPhone && guestPhone.replace(/\D/g, '').length !== 10) ||
                (mapsApiState !== 'failed' && !isPinSet)
              }
              onClick={() => {
                const address = editingAddress ? editedAddress.trim() : (userAddress || guestAddress.trim());
                if (!businessPhone || !address) return;
                const msg = messageMode === 'custom' && customMessage.trim()
                  ? customMessage.trim()
                  : DEFAULT_ORDER_MESSAGE;
                const locationLink = isPinSet && pinCoords ? buildGoogleMapsLink(pinCoords.lat, pinCoords.lng) : undefined;
                const url = buildWhatsAppOrderUrl(businessPhone, address, msg, profileUrl, locationLink, effectivePhone || undefined);
                window.open(url, '_blank', 'noopener,noreferrer');
                if (businessId && effectivePhone) {
                  BusinessApiService.addCustomerToBusiness(businessId, effectivePhone).catch(() => {});
                }
              }}
            >
              <WhatsAppIcon size={20} />
              <span>Order via WhatsApp</span>
            </button>
            )}

            {orderMode !== 'whatsapp' && (
              <NineFindsOrderButton
                businessId={businessId}
                collectionUrl={profileUrl}
                totalPrice={totalPrice ?? 0}
                buyerPhone={effectivePhone}
                fulfillmentType={orderMode === 'nine-delivery' ? 'DELIVERY' : 'ON_PREMISE'}
                deliveryAddress={currentEffectiveAddress}
                deliveryPin={isPinSet ? pinCoords : null}
                isLoggedIn={!!profileData}
                pinOptional={mapsApiState === 'failed'}
                onRequireLogin={() => {
                  onClose();
                  dispatch(openAuth(window.location.pathname + window.location.search));
                }}
                onClose={onClose}
              />
            )}
          </>
        )}

        {step === 'location-pin' && (
          <>
            <button className="share-modal-back-btn" onClick={() => setStep('whatsapp')}>
              <ChevronLeft size={18} />
              <span>Back</span>
            </button>

            <h2 className="share-modal-title">Set Delivery Pin</h2>
            <p className="share-modal-subtitle">Drag the pin to your exact delivery spot</p>

            <LocationPinPicker
              initialAddress={editingAddress ? editedAddress : (userAddress || guestAddress)}
              initialCoords={pinCoords}
              onConfirm={(coords) => { setPinCoords(coords); setPinAddress(currentEffectiveAddress); setStep('whatsapp'); }}
              onCancel={() => setStep('whatsapp')}
            />
          </>
        )}

      </div>
    </div>,
    document.body
  );
};

export default ShareProfileModal;
